import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminUsers } from '../../api/hooks';
import type { AdminUser, UserRoleFilter, UserStatusFilter } from '../../api/types';
import { ErrorState, Loading, TableEmpty } from '../../components/States';
import { useDebounced } from '../../components/useDebounced';
import { fmtDate } from '../../lib/dates';
import { fmtPhone } from '../../lib/phone';
import { UserEditor } from './UserEditor';
import { UserStatusPill } from './UserStatusPill';
import { ViewAsPanel } from './ViewAsPanel';

// Два независимых фильтра: «Врачи» + «Заблокированы» — заблокированные врачи и т.д.
const ROLES: UserRoleFilter[] = ['all', 'doctor', 'tech', 'admin'];
const STATUSES: UserStatusFilter[] = ['all', 'active', 'invited', 'blocked'];

type Panel = { kind: 'edit'; id: string | null } | { kind: 'viewAs'; user: AdminUser } | null;

export function UsersPage() {
  const { t } = useTranslation();
  const [role, setRole] = useState<UserRoleFilter>('all');
  const [status, setStatus] = useState<UserStatusFilter>('all');
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim());
  const [panel, setPanel] = useState<Panel>(null);
  const users = useAdminUsers(role, status, q);
  const rows = users.data?.pages.flatMap((p) => p.items) ?? [];
  const page = users.data?.pages[0];

  const open = (p: Panel) => {
    setPanel(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loginInfo = (u: AdminUser) => {
    if (u.status === 'invited') return t('admin.users.loginInvited');
    return u.last_login_at
      ? t('admin.users.loginLast', { date: fmtDate(u.last_login_at) })
      : t('admin.users.loginNever');
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('admin.users.title')}</h1>
          <p>{t('admin.users.sub')}</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => open({ kind: 'edit', id: null })}>
          {t('admin.users.invite')}
        </button>
      </div>

      {panel?.kind === 'edit' && (
        <UserEditor key={panel.id ?? 'new'} id={panel.id} onClose={() => setPanel(null)} />
      )}
      {panel?.kind === 'viewAs' && (
        <ViewAsPanel key={panel.user.id} user={panel.user} onCancel={() => setPanel(null)} />
      )}

      <div className="toolbar">
        <div className="chips" role="group" aria-label={t('admin.users.roleFilter')}>
          {ROLES.map((k) => (
            <button
              key={k}
              type="button"
              className="chip"
              aria-pressed={role === k}
              onClick={() => setRole(k)}
            >
              {t(`admin.users.chips.${k}`)} {page?.role_counts[k] ?? ''}
            </button>
          ))}
        </div>
        <input
          className="input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('admin.users.search')}
          aria-label={t('admin.users.searchAria')}
          style={{ marginLeft: 'auto' }}
        />
      </div>
      <div className="toolbar">
        <span className="muted small">{t('admin.users.statusLabel')}</span>
        <div className="chips" role="group" aria-label={t('admin.users.statusFilter')}>
          {STATUSES.map((k) => (
            <button
              key={k}
              type="button"
              className="chip"
              aria-pressed={status === k}
              onClick={() => setStatus(k)}
            >
              {t(`admin.users.statusChips.${k}`)} {page?.status_counts[k] ?? ''}
            </button>
          ))}
        </div>
      </div>

      {users.isLoading ? (
        <Loading />
      ) : users.error && !users.data ? (
        <ErrorState error={users.error} onRetry={() => void users.refetch()} />
      ) : !rows.length ? (
        <TableEmpty>{t('admin.users.empty')}</TableEmpty>
      ) : (
        <div className="table-wrap plain stack">
          <table>
            <thead>
              <tr>
                <th>{t('admin.users.cols.user')}</th>
                <th>{t('admin.users.cols.orgs')}</th>
                <th>{t('admin.users.cols.status')}</th>
                <th>{t('admin.users.cols.login')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id}>
                  <td className="wide">
                    <div className="pname">
                      {u.name}
                      {u.is_platform_admin && (
                        <>
                          {' '}
                          <span className="tag">{t('admin.users.adminTag')}</span>
                        </>
                      )}
                      {u.is_self && (
                        <>
                          {' '}
                          <span className="tag">{t('admin.users.selfTag')}</span>
                        </>
                      )}
                    </div>
                    <div className="case-id">
                      {u.email}
                      {u.phone && (
                        <>
                          <br />
                          {fmtPhone(u.phone)}
                        </>
                      )}
                    </div>
                  </td>
                  <td data-label={t('admin.users.cols.orgs')}>
                    {u.memberships.length ? (
                      u.memberships.map((m, i) => (
                        <span key={m.org_id}>
                          {i > 0 && <br />}
                          {m.org_name} <span className="muted small">{t(`admin.roleOf.${m.org_type}`)}</span>
                        </span>
                      ))
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td data-label={t('admin.users.cols.status')}>
                    <UserStatusPill status={u.status} />
                  </td>
                  <td className="small" data-label={t('admin.users.cols.login')}>
                    {loginInfo(u)}
                  </td>
                  <td className="wide">
                    <div className="row-acts">
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => open({ kind: 'edit', id: u.id })}
                      >
                        {t('admin.users.open')}
                      </button>
                      {!u.is_platform_admin && u.status === 'active' && u.memberships.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => open({ kind: 'viewAs', user: u })}
                        >
                          {t('admin.users.viewAs')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.hasNextPage && (
            <div className="more">
              <button
                type="button"
                className="btn"
                disabled={users.isFetchingNextPage}
                onClick={() => void users.fetchNextPage()}
              >
                {t('common.showMore')}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
